import { useState } from 'react';
import { validateDateRange, formatDateForAPI } from '../../services/accident-reports-api';
import './date-range-picker.scss';

interface DateRangePickerProps {
  onSearch: (startDate: string, endDate: string) => void;
  onCancel: () => void;
  language?: 'ar' | 'en' | 'ur';
  isLoading?: boolean;
}

export default function DateRangePicker({
  onSearch,
  onCancel,
  language = 'ar',
  isLoading = false,
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [error, setError] = useState<string>('');

  const texts = {
    ar: {
      title: 'اختر نطاق التاريخ',
      subtitle: 'حدد تاريخ البداية والنهاية للبحث عن التقارير',
      startDate: 'من تاريخ',
      endDate: 'إلى تاريخ',
      search: 'بحث',
      cancel: 'إلغاء',
      searching: 'جاري البحث...',
      errorRequired: 'يرجى اختيار التاريخ',
      errorInvalidRange: 'تاريخ البداية يجب أن يكون قبل تاريخ النهاية',
      errorFuture: 'التاريخ لا يمكن أن يكون في المستقبل',
      note: 'ملاحظة: سيتم البحث عن جميع التقارير في النطاق الزمني المحدد',
    },
    en: {
      title: 'Select Date Range',
      subtitle: 'Choose start and end dates to search for reports',
      startDate: 'From Date',
      endDate: 'To Date',
      search: 'Search',
      cancel: 'Cancel',
      searching: 'Searching...',
      errorRequired: 'Please select a date',
      errorInvalidRange: 'Start date must be before end date',
      errorFuture: 'Date cannot be in the future',
      note: 'Note: All reports within the selected date range will be searched',
    },
    ur: {
      title: 'تاریخ کی حد منتخب کریں',
      subtitle: 'رپورٹس تلاش کرنے کے لیے شروع اور اختتامی تاریخیں منتخب کریں',
      startDate: 'تاریخ سے',
      endDate: 'تاریخ تک',
      search: 'تلاش کریں',
      cancel: 'منسوخ کریں',
      searching: 'تلاش جاری ہے...',
      errorRequired: 'براہ کرم تاریخ منتخب کریں',
      errorInvalidRange: 'شروع کی تاریخ اختتامی تاریخ سے پہلے ہونی چاہیے',
      errorFuture: 'تاریخ مستقبل میں نہیں ہو سکتی',
      note: 'نوٹ: منتخب شدہ تاریخ کی حد میں تمام رپورٹس تلاش کی جائیں گی',
    },
  };

  const t = texts[language];

  // Get today's date in YYYY-MM-DD format for max attribute
  const today = formatDateForAPI(new Date());

  const handleSearch = () => {
    setError('');

    // Validate dates are selected
    if (!startDate || !endDate) {
      setError(t.errorRequired);
      return;
    }

    // Validate date range
    const validation = validateDateRange(startDate, endDate);
    if (!validation.valid) {
      if (validation.error?.includes('before')) {
        setError(t.errorInvalidRange);
      } else if (validation.error?.includes('future')) {
        setError(t.errorFuture);
      } else {
        setError(validation.error || t.errorRequired);
      }
      return;
    }

    // Trigger search
    onSearch(startDate, endDate);
  };

  return (
    <div className="date-range-picker-container">
      <div className="date-range-header">
        <h3>{t.title}</h3>
        <p className="subtitle">{t.subtitle}</p>
      </div>

      <div className="date-inputs">
        <div className="date-field">
          <label htmlFor="startDate">
            {t.startDate}
            <span className="required">*</span>
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              if (error) setError('');
            }}
            max={today}
            disabled={isLoading}
            className={error ? 'error' : ''}
          />
        </div>

        <div className="date-field">
          <label htmlFor="endDate">
            {t.endDate}
            <span className="required">*</span>
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              if (error) setError('');
            }}
            max={today}
            disabled={isLoading}
            className={error ? 'error' : ''}
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          {error}
        </div>
      )}

      <div className="info-box">
        <span className="material-symbols-outlined">info</span>
        <span>{t.note}</span>
      </div>

      <div className="action-buttons">
        <button
          className="cancel-button"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t.cancel}
        </button>
        <button
          className="search-button"
          onClick={handleSearch}
          disabled={isLoading || !startDate || !endDate}
        >
          {isLoading ? (
            <>
              <span className="spinner"></span>
              {t.searching}
            </>
          ) : (
            <>
              <span className="material-symbols-outlined">search</span>
              {t.search}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

